import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FtsGetUsersMeUseCaseService } from '@core/use-cases/auth/fts-get-users-me.use-case.service';
import { AuthStorageService } from '@core/services/auth-storage.service';

import { OverlayContainer } from '@angular/cdk/overlay';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { MAT_LUXON_DATE_ADAPTER_OPTIONS } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { CustomLuxonDateAdapter, MY_DATE_FORMATS } from '@core/adapters/custom-luxon-date-adapter';
import { httpTranslateLoaderFactory } from '@core/i18n/translate-loader';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { httpInterceptor } from '@core/interceptors/http.interceptor';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CustomOverlayContainer } from '@shared/services/custom-overlay-container.service';
import { NgxCurrencyInputMode, provideEnvironmentNgxCurrency } from 'ngx-currency';
import { initialConfig, provideEnvironmentNgxMask } from 'ngx-mask';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import 'chartjs-adapter-date-fns';
import localePt from '@angular/common/locales/pt';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import { registerLocaleData } from '@angular/common';
import { VDO_ICONS_SPRITE } from '@ui/constants/vdo-icons-sprite.const';

registerLocaleData(localePt);
registerLocaleData(localeEn);
registerLocaleData(localeEs);

const defaultRegisterables = withDefaultRegisterables().registerables ?? [];

export const appConfig: ApplicationConfig = {
  providers: [
    FtsGetUsersMeUseCaseService,
    provideAppInitializer(() => {
      const iconRegistry = inject(MatIconRegistry);
      const sanitizer = inject(DomSanitizer);

      iconRegistry.addSvgIconSetLiteral(sanitizer.bypassSecurityTrustHtml(VDO_ICONS_SPRITE));
    }),
    provideAppInitializer(() => {
      const auth = inject(AuthStorageService);
      const getMe = inject(FtsGetUsersMeUseCaseService);

      if (!auth.isAuthenticated()) {
        return;
      }

      return firstValueFrom(
        getMe.execute().pipe(
          catchError(err => {
            console.error('Falha ao inicializar sessão do usuário', err);
            return of(void 0);
          })
        )
      );
    }),
    { provide: OverlayContainer, useClass: CustomOverlayContainer },
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpTranslateLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: 'pt-br',
      }),
    ]),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideEnvironmentNgxMask(() => ({
      patterns: {
        ...initialConfig.patterns,
        'Z': { pattern: /[A-Za-z0-9 ]/ },
      },
    })),

    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: MAT_LUXON_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } },
    {
      provide: DateAdapter,
      useClass: CustomLuxonDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    provideHttpClient(withInterceptors([authInterceptor, httpInterceptor])),
    provideEnvironmentNgxCurrency({
      align: 'left',
      allowNegative: false,
      allowZero: true,
      decimal: ',',
      precision: 2,
      prefix: '',
      suffix: '',
      thousands: '.',
      nullable: false,
      min: 0,
      max: null,
      inputMode: NgxCurrencyInputMode.Financial,
    }),
    provideCharts({
      registerables: [...defaultRegisterables, ChartDataLabels, annotationPlugin],
    }),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideMessaging(() => getMessaging()),
  ],
};
